import { PartitionOutlined } from '@ant-design/icons';
import { UnicornDetectorIcon } from '@mm/icons/AntDesignIcons';
import { useAudiencesCRUDActions } from '@mm/state/actions/AudiencesCRUDActions';
import { TagFilterItem } from '@mm/state/model/AudiencesModel';
import WidgetModel, { NavigateConfig } from '@mm/state/model/WidgetModel';
import { selectAudienceByIdOrIds } from '@mm/state/selectors/messengerAdSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import { uniqueId } from 'lodash';
import React, { ReactNode, useEffect } from 'react';
import { Handle, NodeProps, Position } from 'react-flow-renderer';
import { useSelector } from 'react-redux';
import { useCommonWidgetHandlers } from '../../../../../../../../../common/helpers/widget-helpers/use-common-widget-handlers';
import { useCreateNewAudience } from '../../../../../Audiences/use-create-new-audience';
import { MyLeadsFilterBar } from '../../../../../Contacts/MyLeadsFilterBar/MyLeadsFilterBar';
import {
  prepareEsFiltersStructure,
  unassignFilterParams,
} from '../../../../../Contacts/MyLeadsFiltersPanel/helpers';
import { useFilterParams } from '../../../../../Contacts/MyLeadsFiltersPanel/use-filter-params';
import { useCreateFilterParams } from '../../../../../Contacts/use-create-filter-params';
import { WithAdderButton } from '../Adder/Adder';
import { useOpenOnReady } from '../use-open-on-ready';
import css from './UnicornDetector.module.css';

type Props = {
  className?: string;
  isUnicornPath?: boolean;
} & NodeProps<{ widget: WidgetModel; pageGroupId?: number }>;

export const ConditionIcon = PartitionOutlined;

const noop = () => {};

export const UnicornDetector: React.FC<Props> = ({
  className,
  data,
  isDragging,
  isUnicornPath,
  children,
}) => {
  const chatbotId = useSelector(activeChatbotIdSelector);

  const widgets = useSelector((state: StoreState) => state.widget.entities);
  const widget = widgets.find(w => w.id === data.widget.id) || data.widget;

  const config = widget.config as NavigateConfig;

  const { audienceId } = config;
  const [audience] = useSelector((state: StoreState) =>
    selectAudienceByIdOrIds(state, audienceId || null),
  );

  const commonWidgetHandlers = useCommonWidgetHandlers();
  const { createOrEditAudienceAndOpenMenu } = useCreateNewAudience();
  const audiencesCRUDActions = useAudiencesCRUDActions();

  const { filterParams, setFilterParams } = useCreateFilterParams({
    query: prepareEsFiltersStructure(audience?.esFilters) || {},
    filterParams: unassignFilterParams(audience?.filterParams),
    tagsFilters: unassignFilterParams(audience?.tagsFilters) as TagFilterItem[],
    page: 1,
  });
  const { filtersCount } = useFilterParams({
    filterParams,
  });

  const onClose = (audienceId: number) => {
    if (!widget.config.audienceId && audienceId) {
      try {
        commonWidgetHandlers.update({
          ...widget,
          config: {
            ...config,
            audienceId,
          } as any,
        });
      } catch (err) {}
    }
  };

  const onClick = async () => {
    console.debug(`Condition: onClick: `, { data, widget });

    if (isDragging) {
      return;
    }

    await createOrEditAudienceAndOpenMenu({
      activeAudience: audience,
      filterParams,
      setFilterParams,
      title: 'Unicorn Detector',
      buttonName: 'Save',
      withSaveAsNew: false,
      hiddenAudience: true,
      onSave: onClose,
    });
  };

  useOpenOnReady(widget, onClick);

  useEffect(() => {
    console.debug('Condition: useEffect');

    (async () => {
      if (audienceId) {
        await audiencesCRUDActions.retrieveAudienceById(chatbotId, audienceId);
      }
    })();
  }, []);

  return (
    <>
      <div className={classNames(className, css.container)} onClick={onClick}>
        <Handle type="target" position={Position.Top} />

        <div className={css.body}>
          <div className={css.iconContainer}>
            <div className={css.icon}>
              <UnicornDetectorIcon size={148} />
            </div>
          </div>
          <div className={css.contentContainer}>
            <div>
              {React.Children.map<ReactNode, ReactNode>(children, child => {
                if (React.isValidElement(child)) {
                  return React.cloneElement(child, {
                    // @ts-ignore onEdit does not seemto be present or types are off in @types/react
                    onEdit: onClick,
                    key: uniqueId(),
                  });
                }
              })}
            </div>

            <div className={css.header}>
              <span className={css.placeholder}>UNICORN DETECTOR</span>
            </div>

            <div className={css.conditions}>
              {!filtersCount && (
                <span className={css.condition}>
                  <span className={css.filter}>Please add condition</span>
                </span>
              )}

              {filtersCount < 4 ? (
                <div className={css.condition}>
                  <MyLeadsFilterBar
                    filterParams={filterParams}
                    setFilterParams={setFilterParams}
                    withReset={false}
                  />
                </div>
              ) : (
                <div className={css.conditionsCollapsed}>
                  {filtersCount} filters
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-evenly',
          width: '200px',
          margin: '0 auto',
        }}
      >
        <WithAdderButton
          pageId={data.widget.config.pageId}
          sortOrder={0}
          pageGroupId={data.pageGroupId}
          leadType="unicorn"
          widgetId={data.widget.id}
        >
          <Handle id="yes" type="source" position={Position.Bottom} />
        </WithAdderButton>

        <WithAdderButton
          pageId={data.widget.config.oppositePageId}
          sortOrder={0}
          pageGroupId={data.pageGroupId}
          leadType="donkey"
          widgetId={data.widget.id}
        >
          <Handle id="no" type="source" position={Position.Bottom} />
        </WithAdderButton>
      </div>
    </>
  );
};
